import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question } from "./_schema";
import { buildReframeBody, deriveWeightlossV2Result } from "./quiz-weightloss-v2-profile";

const QUESTION_ORDER = [
  "age",
  "height",
  "weight",
  "fat-location",
  "goal",
  "belly-diet",
  "chest-change",
  "exercise",
  "tried",
  "info-reframe",
  "impact",
  "weight-look",
  "duration",
  "info-analyzing",
  "signal-summary",
  "event-goal",
  "event-date",
  "info-mechanism",
] as const;

const questions: Question[] = [
  {
    id: "age",
    type: "single",
    // Both the deck's wording, restored once the intro moved to its own screen
    // (`meta.introCta`). While the intro stacked on top of this question the
    // pair cost ~97px of a 10px budget and had to be cut; with the intro gone
    // from this screen the pills sit at 300/368 against a 628 fold.
    prompt: "First, how old are you?",
    helper: "We use your age to read the rest of your answers in context.",
    options: [
      { value: "20-29", label: "20–29" },
      { value: "30-39", label: "30–39" },
      { value: "40-49", label: "40–49" },
      { value: "50-plus", label: "50+" },
    ],
  },
  {
    id: "height",
    type: "ruler",
    prompt: "How tall are you?",
    helper: "This helps us read your answers in context. No judgment.",
    min: 48,
    max: 84,
    step: 1,
    defaultValue: 70,
    unit: "in",
    valueFormat: "feet-inches",
    altUnit: { label: "cm", factor: 0.393701 },
    analyticsAnswerMode: "presence",
  },
  {
    id: "weight",
    type: "ruler",
    prompt: "And your weight right now?",
    helper: "No judgment. This is simply the starting point we build your plan from.",
    min: 90,
    max: 400,
    step: 1,
    defaultValue: 210,
    unit: "lbs",
    altUnit: { label: "kg", factor: 2.20462 },
    analyticsAnswerMode: "presence",
  },
  {
    id: "fat-location",
    type: "single",
    prompt: "Where does your body hold fat the most?",
    options: [
      { value: "belly", label: "My belly and lower gut" },
      { value: "chest", label: "My chest has gone soft or puffy" },
      { value: "love-handles", label: "My love handles and lower back" },
      { value: "everywhere", label: "It's spread out everywhere" },
    ],
  },
  {
    id: "goal",
    type: "single",
    prompt: "What would you most like to change first?",
    helper: "So we can build the plan around what matters most to you.",
    options: [
      { value: "belly", label: "A flatter belly" },
      { value: "chest", label: "A firmer-looking chest" },
      { value: "waist", label: "A leaner waist" },
      { value: "energy", label: "More energy and drive" },
    ],
  },
  {
    id: "belly-diet",
    type: "single",
    prompt: "When you diet, what does your belly do?",
    options: [
      { value: "last", label: "It's the last thing to go, if it goes at all" },
      { value: "stalls", label: "It shrinks a little, then stalls" },
      { value: "everywhere-else", label: "I lose everywhere but there" },
      { value: "fine", label: "It comes off fine" },
    ],
  },
  {
    id: "chest-change",
    type: "single",
    prompt: "Has your chest become softer, puffier, or fuller than it used to be?",
    options: [
      { value: "yes", label: "Yes, it's bothered me for a while" },
      { value: "little", label: "A little" },
      { value: "unsure", label: "I'm not sure" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "exercise",
    type: "single",
    prompt: "How often do you exercise?",
    helper: "So we can see how your body responds to the work you're already putting in.",
    options: [
      { value: "4-plus", label: "4+ times a week" },
      { value: "2-3", label: "2–3 times a week" },
      { value: "weekly", label: "Once a week or so" },
      { value: "rarely", label: "Rarely these days" },
    ],
  },
  {
    id: "tried",
    type: "multi",
    prompt: "Which of these have you already tried?",
    helper: "Select all that apply.",
    min: 1,
    exclusiveValues: ["none"],
    options: [
      { value: "fasting", label: "Intermittent fasting" },
      { value: "keto", label: "Keto or low-carb" },
      { value: "calories", label: "Counting calories" },
      { value: "medication", label: "Weight-loss supplements or medication" },
      { value: "none", label: "None of these yet" },
    ],
  },
  {
    // ABSOLUTION BRIDGE -- the approved prototype's `renderReframe` screen.
    //
    // Same structural beat v1 carries as `info-absolution` (see
    // quizzes/quiz-weightloss.ts): after the user has confessed everything he
    // has already tried, take the blame off him before asking what the pattern
    // has cost. It has to sit between `tried` and `impact` for that to land --
    // "you tried, it failed, it was not you" -> "here is what it has cost you".
    //
    // Prefixed `info-` to match this file's other pass-through screens
    // (`info-analyzing`, `info-mechanism`); the id is an analytics key, and the
    // prefix is what tells a funnel reader the row is a screen, not an answer.
    //
    // The deck opens this screen by reading the user's own `exercise` and
    // `tried` answers back to him ("You train, and you stay consistent about
    // it. You've tried keto or low-carb and counting calories."), which is what
    // `deriveBody` below restores -- a pure function of `answers`, so the
    // screen stays reproducible from the persisted submission (rule 2).
    //
    // `body` is the static fallback and is deliberately true on every path:
    // it renders if the seam is ever removed, and it is what a reader of this
    // file sees as the screen's canonical text.
    id: "info-reframe",
    type: "info",
    heading: "It Was Never Your Willpower. And It Was Never You.",
    body: [
      "You've put in the work, and the belly and chest still refuse to change. Your answers may be pointing to a pattern that calories and workouts alone don't explain.",
      "A few more questions will show how long it's been happening and what your next 90 days should focus on.",
    ],
    deriveBody: buildReframeBody,
    cta: "Continue",
    // No `stickyCta`: two short paragraphs, so the in-flow CTA clears the
    // iPhone SE fold with room to spare. `info-mechanism` stays the only pinned
    // screen in the repo (locked by tests/quizzes/quiz-weightloss-v2.test.ts).
  },
  {
    id: "impact",
    type: "single",
    prompt: "Which of these has taken the biggest hit lately?",
    options: [
      { value: "energy", label: "Energy" },
      { value: "sex-drive", label: "Sex drive" },
      { value: "motivation", label: "Motivation" },
      { value: "all", label: "All three" },
      { value: "none", label: "None of these" },
      { value: "unsure", label: "Not sure" },
    ],
  },
  {
    id: "weight-look",
    type: "single",
    prompt: "The last time you dropped weight, how did you end up looking?",
    options: [
      { value: "smaller-soft", label: "Smaller, but still soft and never actually lean" },
      { value: "not-defined", label: "A bit better, but not defined" },
      { value: "lean", label: "Pretty lean" },
      { value: "not-lost", label: "I haven't been able to lose much at all" },
    ],
  },
  {
    id: "duration",
    type: "single",
    prompt: "How long have you been fighting this?",
    options: [
      { value: "years", label: "More than a few years" },
      { value: "year", label: "About a year" },
      { value: "months", label: "The past few months" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "info-analyzing",
    type: "info",
    heading: "Putting Your Answers Together…",
    // The approved copy deck shows this beat as three sequential steps
    // ("Reading the signs you reported... -> Reading your fat-storage
    // pattern... -> Building your assessment..."). An `info` screen renders
    // `body` as stacked paragraphs rather than ticking rows, so the three steps
    // are three paragraphs; the 4s `autoAdvanceMs` beat below is what gives
    // them their sense of sequence.
    body: [
      "Reading the signs you reported…",
      "Reading your fat-storage pattern…",
      "Building your assessment…",
    ],
    animation: "analyzing",
    autoAdvanceMs: 4000,
    // No `cta`, matching the prototype's `renderProcessing` (which renders no
    // forward button at all) and every other analyzing interstitial in the
    // repo. `InfoScreen` drops the button only when `cta` is undefined, so
    // authoring one here put a skip affordance over the 4s beat that sells the
    // diagnostic. Locked registry-wide by tests/quizzes/quiz-weightloss-v2.test.ts.
  },
  {
    id: "signal-summary",
    type: "signal-summary",
    eyebrow: "Results · 1 of 2",
    heading: "Your Hormone-Related Fat-Storage Assessment",
    helper: "Based on the signs you reported. This is a self-assessment, not a lab result.",
    cta: "Show me my plan",
    testimonial: {
      quote:
        "My total testosterone went from 330 to 742. Natural, no needles. And the belly finally started responding.",
      author: "Zach R.",
      badge: "Lab-verified",
      // A DIFFERENT face from the intro screen's (which uses the before-trt
      // avatar for "David R."). Two quotes attributed to two named members on
      // the same run must not share a portrait. Both are the ~13 KB 256x256
      // WebPs, never the 10 MB default (rule 19).
      avatar: "/static/images/quiz-glp-testimonial-avatar.webp",
    },
  },
  {
    id: "event-goal",
    type: "single",
    prompt: "What are you working toward?",
    helper: "Give us a finish line so we can pace your next 90 days around it.",
    options: [
      { value: "vacation", label: "A vacation or trip" },
      { value: "wedding", label: "A wedding or reunion" },
      { value: "birthday", label: "A milestone birthday" },
      { value: "bloodwork", label: "A health check or bloodwork" },
      { value: "myself", label: "Just getting back to myself" },
    ],
  },
  {
    id: "event-date",
    type: "date",
    prompt: "When's your special event?",
    helper: "We'll pace your 90-day plan around it.",
    noDateLabel: "I don't have a set date",
    noDateValue: "no-date",
  },
  {
    id: "info-mechanism",
    type: "info",
    heading: "Here's One Possible Reason the Belly Can Stay Stubborn",
    body: [
      "Fat tissue is involved in the aromatase pathway, where testosterone can be converted into estrogen. That may help explain why some men see the scale move without getting the shape they want.",
      "And a standard total-testosterone result may not tell the whole story about how much testosterone is available for use.",
      "Your plan is built around supporting the system behind the pattern your answers pointed to.",
    ],
    cta: "See my plan",
    // Three dense paragraphs push an in-flow CTA below the iPhone SE fold, so
    // the forward action is pinned to the content column instead of appearing
    // only after the whole explanation has been scrolled. The only screen in
    // the repo that opts in.
    stickyCta: true,
  },
];

export const quizWeightlossV2Quiz = defineQuiz({
  id: "quiz-weightloss-v2",
  slug: "quiz-weightloss-v2",
  meta: {
    title: "Mars Men Weight-Loss Assessment",
    introHeadline: "The Belly And Soft Chest That Won't Budge May Have A Hormonal Reason.",
    introAccent: "Hormonal Reason",
    // Promotes the intro to a screen of its own, as the approved deck and the
    // prototype's `flow` array both specify ("intro" then "age", with the
    // deck's "QUESTION 1 OF 14" on the screen after "Start My Assessment").
    // This is what lets the three fields below carry the deck's full wording:
    // stacked on question 1 they put the first answer pill 111px below the
    // iPhone SE fold. See `introCta` in quizzes/_schema.ts.
    introCta: "Start my assessment",
    // The prototype's own chip labels, restored at full length. They were
    // abbreviated ("2-Min Assessment") only because the row had to fit beside
    // a question on one screen; on a dedicated screen the 65.2px they bill is
    // free.
    introBadges: ["2-Minute Assessment", "90-Day Plan"],
    // The deck's full lede, restored. It was cut to 44 characters when the
    // intro shared screen 1 with the age question -- it wraps to five lines
    // (130px), which that layout could not afford and this one can.
    introBody:
      "A quiet hormone shift can work against your belly and chest, so the harder you diet, the less it seems to move. Take the assessment to see what your reported signs point to, and what your next 90 days should focus on.",
    testimonial: {
      // The deck's wording. "This finally explained why" keeps the bare
      // demonstrative the house entry-testimonial rule would normally swap for
      // "This quiz" -- on a standalone intro screen the referent is
      // unambiguous, since the quote sits directly under the START button.
      quote:
        "Cut calories hard for months and the scale wouldn't move. This finally explained why in about two minutes.",
      author: "David R., Verified Buyer",
      avatar: "/static/images/quiz-before-trt-testimonial-avatar.webp",
    },
  },
  questions,

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const index = QUESTION_ORDER.indexOf(
      currentQuestionId as (typeof QUESTION_ORDER)[number],
    );
    if (index < 0) return null;
    return QUESTION_ORDER[index + 1] ?? null;
  },

  classifier(_answers: Answers): PersonaKey {
    return "everyday-man";
  },

  postQuiz: {
    kind: "weightloss-v2",
    deriveResult: deriveWeightlossV2Result,
    submitOnApplying: true,
    plan: {
      eyebrow: "Results · 2 of 2",
      cta: "Get my protocol",
    },
    offer: {
      kicker: "Your offer",
      heading: "Start Your 90-Day Higher-T Protocol",
      lede:
        "Based on where you're starting, the 30-day starter kit is the first step in the full 90-day protocol.",
      productName: "Natural Testosterone Support",
      productDescription:
        "A potent natural testosterone stack built to support production, free available T, protect it, and help your body use it effectively.",
      // A NEW FILENAME, never an overwrite. Static assets are long-cached on
      // the widget CDN (max-age 86400 + 7d stale-while-revalidate), so swapping
      // bytes under a live path leaves returning visitors on the old hero for
      // days. `deploy_static.sh` syncs without `--delete`, so every superseded
      // object survives in S3 for anyone mid-cache; rollback is this one line.
      //
      // This is the DISCOUNT-NEUTRAL 1112x1112 product master, chosen over the
      // promo renders on 2026-08-11. Every export carrying the burned-in
      // "GET 67% OFF" badge (576x556, and the 336x336 the operator later
      // supplied) is framed so tightly that the bottle's reflection is severed
      // mid-fall, leaving a hard seam with a second partial bottle under it --
      // the "image doubling" the operator reported. Trimming at the seam cured
      // the doubling but ended the frame on a hard pedestal band, and both
      // promo exports also letterbox inside this surface's square card. The
      // master is a true square that fills the card exactly, its reflection
      // fades naturally, and at 1112px it is sharp through 3x DPR.
      imageSrc: "/static/images/mm-weightloss-v2-product-1112.webp",
      // Alt describes what is DEPICTED, and the artwork carries no discount --
      // do not reintroduce a percentage here. That is now strictly safer: the
      // offer's discount lives only in copy (`cta` / `pricePoints` /
      // `howItWorksBody`), so changing it never requires re-rendering artwork
      // and can never contradict a stale cached hero.
      imageAlt:
        "Mars Men Natural Testosterone Support, 150 capsules for a 30-day supply. 80% of men recommend Mars Men, based on a 12 week triple-blind clinical study with 100 participants.",
      // NO `price` / `compareAtPrice`, DELIBERATELY (operator decision,
      // 2026-08-11: "just say 67% OFF for now, don't mention pricing"). Both
      // fields are optional and `WeightlossOffer` drops the whole price row
      // when they are absent, so this surface states the discount and lets the
      // PDP be the only place a figure is quoted.
      //
      // That is also the safer arrangement for THIS product. Read 2026-08-10
      // off the rendered storefront at /products/30-day-supply-starter-kit, the
      // promo is "SUBSCRIBE & SAVE 67% OFF ON YOUR FIRST ORDER" / "$39 today,
      // $59 for life" -- but $39 is NOT in the Shopify variant data. The Admin
      // API still returns price 5900 / compareAtPrice 11800 and a 0% recurring
      // adjustment on the "Delivered every 30 days" selling plan, so the
      // first-order price is applied above the product layer and cannot be
      // verified from the product API the way `rating` below can. Quoting it
      // here would have meant two systems that can silently disagree about what
      // the customer pays.
      //
      // If a price is ever restored: the product is `requiresSellingPlan:
      // false` and the PDP also sells it one-time at "$118 + $7 Shipping", so
      // any figure has to be scoped to subscribe-and-save and shown beside the
      // recurring price. Do not restore an unconditional one.
      // PROVENANCE: re-read on 2026-08-10 off the live product
      // /products/30-day-supply-starter-kit (gid 9396598669609) via the Shopify
      // Admin API. `reviews.rating` = {scale_min 1, scale_max 5, value 4.77};
      // `reviews.rating_count` = 5009. Both are populated by Junip. The
      // previous "5 out of 5" star row was a component constant with no source,
      // and 4,925 was not the store's count. "Verified" is dropped: the store's
      // review data includes entries flagged verified_buyer=false, so the whole
      // count cannot be called verified.
      //
      // THESE VALUES DRIFT -- the count moved 4,959 -> 5,009 in the four days
      // between the 2026-08-06 and 2026-08-10 reads. There is no sync job:
      // re-read the two metafields periodically and update this block. If a
      // figure is ever unsourceable, delete the field rather than carrying a
      // stale or invented number; both are optional and the star row simply
      // does not render without them.
      rating: { value: 4.77, outOf: 5 },
      reviewCount: "5,009 reviews",
      // The "over 400,000 customers" chip moved to the plan page's trust strip,
      // where the approved copy deck puts the customer-count proof. Kept off
      // the offer so the same claim is not made twice in two screens.
      //
      // The deck's own wording there is "Trusted by 503,128+ men". That figure
      // is NOT shipped: docs/product-brief.md sets the customer count at "over
      // 400,000" and explicitly says never to disclose exact internal counts.
      // A precise-looking 503,128 is both unsourced and against that rule, so
      // the strip carries the brief's sanctioned phrasing instead.
      featuresLabel: "Restoration, not replacement",
      // The deck's four positioning bullets, replacing the earlier
      // spec-sheet list. Each is sourced: "no shutdown" and the
      // natural-not-synthetic framing are the brief's own TRT counter-position;
      // "no lifelong prescription" follows from the same section.
      features: ["No needles", "No shutdown", "No lifelong prescription", "Natural formula"],
      // Subscription framing, matched to the rendered PDP on 2026-08-10 ("
      // SUBSCRIBE & SAVE 67% OFF ON YOUR FIRST ORDER" / "$39 today, $59 for
      // life" / "Save 50% for life"), with every figure removed per the
      // no-pricing decision above.
      //
      // What survives the figure removal is the SCOPE, and it has to: the PDP
      // names the subscription explicitly, so 67% is the first order and the
      // ongoing rate is half price. Stating "67% off" without "your first
      // order" would widen a promo the storefront does not offer, whether or
      // not a dollar amount is attached.
      //
      // CAVEAT worth re-reading before editing: the product is
      // `requiresSellingPlan: false`, and the PDP also sells it one-time. The
      // auto-ship terms AND the 67% are what the customer gets if he takes the
      // subscription option, not unconditional facts of the offer. Do not
      // reword this into an unconditional claim.
      howItWorksBody:
        "How it works: take the subscribe-and-save option and your first 30-day supply ships now at 67% off, with free shipping and free gifts. After that a fresh 30-day supply arrives every 30 days at half the regular price, and you can skip, pause, or cancel anytime.",
      formula: {
        label: "Built around the full formula",
        body:
          // The deck's tail was "buried in a $19 proprietary blend". The figure
          // is dropped with the rest: on a screen carrying no other currency, a
          // lone "$19" is read as OUR price at a glance, which is the opposite
          // of the line's point. Restore it only if prices return here.
          "Tongkat Ali 1,000mg, Shilajit 400mg, Boron, Fenugreek, and 4 more ingredients in one bottle. Not 50mg of Tongkat buried in a cheap proprietary blend.",
      },
      // Terms, not prices. Each line stays scoped to the order it applies to.
      pricePoints: [
        "67% off your first order with subscribe and save",
        "Then half price for life",
        "Free shipping and free gifts on your first order",
      ],
      guarantee: {
        label: "90-Day Higher-T Guarantee",
        // The approved deck's sentence, with ONE deliberate deviation: it reads
        // "if your body doesn't respond", and this ships "if you are not
        // satisfied".
        //
        // docs/product-brief.md states the actual terms as "full refund if not
        // satisfied". Gating the refund on a physiological outcome is NARROWER
        // than the real policy -- a customer who responded but is unhappy would
        // read himself as ineligible when he is not. Understating a guarantee
        // still misstates it, and that version misstates it against the
        // customer, so the deck's wording is not restored here. Everything else
        // in the sentence is the deck's.
        body:
          "It's all backed by the 90-Day Higher-T Guarantee: try it risk-free for 90 days, and if you are not satisfied, you get 100% of your money back.",
      },
      // The prototype's closing `survey-proof` line, restored 2026-08-08 after
      // it shipped missing. Unlike the offer's other dropped claims (3M+
      // bottles, 500K+ customers, five solid stars) this one is sourced:
      // docs/product-brief.md reads "Survey of 1,000 customers: 91% report
      // higher energy, 87% noticed increased strength, 82% experienced improved
      // male vitality."
      //
      // The prototype said "1,000 men". The brief's noun is "customers", and
      // the brief wins -- the surveyed population is whatever the source says
      // it is. Same discipline as `rating` above: ship the sourced figure or
      // ship nothing.
      surveyProof:
        "In a survey of 1,000 customers: 91% reported higher energy, 87% reported more strength, 82% reported better vitality.",
      ingredients: [
        {
          name: "Tongkat Ali",
          dose: "1,000 mg",
          imageSrc: "https://mengotomars.com/cdn/shop/files/ing-1.png?v=1782228618&width=354",
          imageAlt: "Tongkat Ali",
        },
        {
          name: "Shilajit",
          dose: "400 mg",
          imageSrc: "https://mengotomars.com/cdn/shop/files/ing-2.png?v=1782228612&width=354",
          imageAlt: "Shilajit",
        },
        {
          name: "Fenugreek",
          dose: "675 mg",
          imageSrc: "https://mengotomars.com/cdn/shop/files/ing-3.png?v=1782228629&width=354",
          imageAlt: "Fenugreek",
        },
        {
          name: "Boron",
          dose: "4 mg",
          imageSrc: "https://mengotomars.com/cdn/shop/files/ing-4.png?v=1782228599&width=354",
          imageAlt: "Boron",
        },
      ],
      cta: "Claim my 67% off",
      href: "https://mengotomars.com/products/30-day-supply-starter-kit",
    },
  },
});
